import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { apiService, AdminUser } from '../../services/apiService';
import { adminTheme } from '../styles/adminTheme';
import { useToast } from '../components/Toast';
import { ConfirmDialog } from '../components/ConfirmDialog';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${adminTheme.colors.text};
  font-weight: 600;
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 6px;
  font-size: 13px;
  width: 280px;
  outline: none;
  &:focus { border-color: ${adminTheme.colors.primary}; }
`;

const CreateBtn = styled.button`
  padding: 8px 16px;
  background: ${adminTheme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const StatBox = styled.div`
  background: white;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 8px;
  padding: 14px 20px;
  min-width: 120px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${adminTheme.colors.textSecondary};
  text-transform: uppercase;
  margin-top: 2px;
`;

const TableContainer = styled.div`
  background: white;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  color: ${adminTheme.colors.textSecondary};
  text-transform: uppercase;
  border-bottom: 2px solid ${adminTheme.colors.border};
  background: #fafbfc;
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  border-bottom: 1px solid ${adminTheme.colors.border};
`;

const Tr = styled.tr`
  &:nth-child(even) { background: #f9fafb; }
  &:hover { background: #f0f4ff; }
`;

const Badge = styled.span<{ $variant: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${p =>
    p.$variant === 'staff' ? '#e3f2fd' :
    p.$variant === 'active' ? '#e8f5e9' :
    '#fce4ec'};
  color: ${p =>
    p.$variant === 'staff' ? '#1565c0' :
    p.$variant === 'active' ? '#2e7d32' :
    '#c62828'};
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  padding: 4px 10px;
  border: 1px solid ${p => p.$danger ? '#d93025' : adminTheme.colors.border};
  background: white;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  color: ${p => p.$danger ? '#d93025' : adminTheme.colors.text};
  margin-left: 6px;
  &:hover {
    background: ${p => p.$danger ? '#fce4ec' : '#f5f5f5'};
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: ${adminTheme.colors.textSecondary};
  font-size: 14px;
`;

// Create User Modal styles
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalDialog = styled.div`
  background: white;
  border-radius: ${adminTheme.borderRadius};
  padding: 24px;
  min-width: 400px;
  max-width: 480px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${adminTheme.colors.textSecondary};
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${adminTheme.colors.primary}; }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${p => p.$primary ? adminTheme.colors.primary : adminTheme.colors.border};
  background: ${p => p.$primary ? adminTheme.colors.primary : 'white'};
  color: ${p => p.$primary ? 'white' : adminTheme.colors.text};
  &:hover {
    opacity: ${p => p.$primary ? 0.9 : 1};
    background: ${p => p.$primary ? undefined : '#f8f9fa'};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorText = styled.div`
  color: ${adminTheme.colors.danger};
  font-size: 12px;
  margin-top: 4px;
`;

function generatePassword(length = 16): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%&*-_=+';
  const all = upper + lower + digits + special;

  // Guarantee at least one of each type
  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    special[Math.floor(Math.random() * special.length)],
  ];

  const rest = Array.from({ length: length - required.length }, () =>
    all[Math.floor(Math.random() * all.length)]
  );

  // Shuffle
  const chars = [...required, ...rest];
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<AdminUser | null>(null);
  const toast = useToast();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUsers(search || undefined);
      setUsers(data);
    } catch (e: any) {
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, [search, toast]);

  useEffect(() => {
    const timer = setTimeout(loadUsers, 300);
    return () => clearTimeout(timer);
  }, [loadUsers]);

  const handleToggleActive = async (user: AdminUser) => {
    try {
      await apiService.updateAdminUser(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      loadUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const handleToggleStaff = async (user: AdminUser) => {
    try {
      await apiService.updateAdminUser(user.id, { is_staff: !user.is_staff });
      toast.success(`Staff status ${user.is_staff ? 'removed' : 'granted'}`);
      loadUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteAdminUser(deleteTarget.id);
      toast.success('User deleted');
      setDeleteTarget(null);
      loadUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleCreateSuccess = () => {
    setShowCreate(false);
    toast.success('User created');
    loadUsers();
  };

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.is_active).length;
  const staffUsers = users.filter(u => u.is_staff).length;

  return (
    <div>
      <PageHeader>
        <Title>User Management</Title>
        <HeaderActions>
          <SearchInput
            placeholder="Search by email, name, or company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <CreateBtn onClick={() => setShowCreate(true)}>+ Create User</CreateBtn>
        </HeaderActions>
      </PageHeader>

      <StatsRow>
        <StatBox>
          <StatValue>{totalUsers}</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>{activeUsers}</StatValue>
          <StatLabel>Active</StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>{staffUsers}</StatValue>
          <StatLabel>Staff</StatLabel>
        </StatBox>
      </StatsRow>

      <TableContainer>
        {loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : users.length === 0 ? (
          <EmptyState>No users found</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Company</Th>
                <Th>Status</Th>
                <Th>Circuits</Th>
                <Th>Simulations</Th>
                <Th>Joined</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <Tr key={user.id}>
                  <Td>{user.email}</Td>
                  <Td>{user.first_name} {user.last_name}</Td>
                  <Td>{user.company || '-'}</Td>
                  <Td>
                    {user.is_active ? (
                      <Badge $variant="active">Active</Badge>
                    ) : (
                      <Badge $variant="inactive">Inactive</Badge>
                    )}
                    {user.is_staff && (
                      <Badge $variant="staff" style={{ marginLeft: 4 }}>Staff</Badge>
                    )}
                  </Td>
                  <Td>{user.circuit_count}</Td>
                  <Td>{user.simulation_count}</Td>
                  <Td>{new Date(user.date_joined).toLocaleDateString()}</Td>
                  <Td>
                    <ActionBtn onClick={() => handleToggleActive(user)}>
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </ActionBtn>
                    <ActionBtn onClick={() => handleToggleStaff(user)}>
                      {user.is_staff ? 'Remove Staff' : 'Make Staff'}
                    </ActionBtn>
                    <ActionBtn onClick={() => setPasswordTarget(user)}>
                      Password
                    </ActionBtn>
                    <ActionBtn $danger onClick={() => setDeleteTarget(user)}>
                      Delete
                    </ActionBtn>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      {deleteTarget && (
        <ConfirmDialog
          title="Delete User"
          message={`Are you sure you want to delete ${deleteTarget.email}? This will also delete all their projects, circuits, and custom components.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmLabel="Delete"
          danger
        />
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {passwordTarget && (
        <ChangePasswordModal
          user={passwordTarget}
          onClose={() => setPasswordTarget(null)}
          onSuccess={() => {
            setPasswordTarget(null);
            toast.success('Password updated');
          }}
        />
      )}
    </div>
  );
};

const PasswordFieldRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  flex: 1;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  letter-spacing: 0.5px;
`;

const SmallBtn = styled.button`
  padding: 6px 10px;
  border: 1px solid ${adminTheme.colors.border};
  background: white;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  color: ${adminTheme.colors.text};
  &:hover { background: #f5f5f5; }
`;

const CopiedHint = styled.span`
  font-size: 11px;
  color: ${adminTheme.colors.success};
  font-weight: 500;
  min-width: 48px;
`;

const PasswordField: React.FC<{
  value: string;
  onChange: (val: string) => void;
}> = ({ value, onChange }) => {
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const pw = generatePassword();
    onChange(pw);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <PasswordFieldRow>
        <PasswordInput
          type="text"
          value={value}
          onChange={e => { onChange(e.target.value); setCopied(false); }}
          placeholder="Enter or generate password"
        />
        <SmallBtn type="button" onClick={handleGenerate} title="Generate random password">
          Generate
        </SmallBtn>
        <SmallBtn type="button" onClick={handleCopy} title="Copy to clipboard">
          Copy
        </SmallBtn>
        {copied && <CopiedHint>Copied!</CopiedHint>}
      </PasswordFieldRow>
    </>
  );
};

const CreateUserModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!username.trim() || !email.trim() || !password.trim()) {
      setError('Username, email, and password are required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      await apiService.createAdminUser({
        username: username.trim(),
        email: email.trim(),
        password,
        company: company.trim(),
      });
      onSuccess();
    } catch (e: any) {
      const msg = e?.message || 'Failed to create user';
      setError(msg);
    }
    setSaving(false);
  };

  return (
    <Overlay onClick={onClose}>
      <ModalDialog onClick={e => e.stopPropagation()}>
        <ModalTitle>Create User</ModalTitle>
        <FormGroup>
          <Label>Username *</Label>
          <Input value={username} onChange={e => setUsername(e.target.value)} autoFocus />
        </FormGroup>
        <FormGroup>
          <Label>Email *</Label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>Password * (min 8 characters)</Label>
          <PasswordField value={password} onChange={setPassword} />
        </FormGroup>
        <FormGroup>
          <Label>Company</Label>
          <Input value={company} onChange={e => setCompany(e.target.value)} />
        </FormGroup>
        {error && <ErrorText>{error}</ErrorText>}
        <ModalActions>
          <ModalBtn onClick={onClose}>Cancel</ModalBtn>
          <ModalBtn $primary onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating...' : 'Create User'}
          </ModalBtn>
        </ModalActions>
      </ModalDialog>
    </Overlay>
  );
};

const ChangePasswordModal: React.FC<{
  user: AdminUser;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ user, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!password.trim()) {
      setError('Password is required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      await apiService.adminSetPassword(user.id, password);
      onSuccess();
    } catch (e: any) {
      const msg = e?.message || 'Failed to update password';
      setError(msg);
    }
    setSaving(false);
  };

  return (
    <Overlay onClick={onClose}>
      <ModalDialog onClick={e => e.stopPropagation()}>
        <ModalTitle>Change Password</ModalTitle>
        <FormGroup>
          <Label>User</Label>
          <div style={{ fontSize: 13, color: '#333', padding: '4px 0' }}>
            {user.email}
          </div>
        </FormGroup>
        <FormGroup>
          <Label>New Password (min 8 characters)</Label>
          <PasswordField value={password} onChange={setPassword} />
        </FormGroup>
        {error && <ErrorText>{error}</ErrorText>}
        <ModalActions>
          <ModalBtn onClick={onClose}>Cancel</ModalBtn>
          <ModalBtn $primary onClick={handleSubmit} disabled={saving}>
            {saving ? 'Updating...' : 'Update Password'}
          </ModalBtn>
        </ModalActions>
      </ModalDialog>
    </Overlay>
  );
};
